import * as React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { webauthnApi, type WebAuthnCredentialDto, getAccessToken } from '@/lib/api';
import { isStandaloneDisplay } from '@/lib/pwa';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { toast } from 'sonner';

/**
 * Settings section: enroll / list / revoke Face ID credentials.
 * Sensitive actions require elevated auth (Face ID on PWA, Google on web).
 */
export function SecuritySettingsCard() {
  const {
    isFaceIdEnrolled,
    faceIdLabel,
    canOfflineUnlock,
    enrollFaceId,
    disableFaceId,
    requireElevatedAuth,
    sessionState,
  } = useAuth();

  const [credentials, setCredentials] = React.useState<WebAuthnCredentialDto[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = React.useState(false);
  const [revokeId, setRevokeId] = React.useState<string | null>(null);
  const standalone = isStandaloneDisplay();
  const online = typeof navigator !== 'undefined' && navigator.onLine;

  const loadCredentials = React.useCallback(async () => {
    if (!online || !getAccessToken() || sessionState !== 'onlineAuthenticated') {
      setCredentials([]);
      return;
    }
    setLoading(true);
    try {
      const list = await webauthnApi.listCredentials();
      setCredentials(Array.isArray(list) ? list : []);
    } catch {
      setCredentials([]);
    } finally {
      setLoading(false);
    }
  }, [online, sessionState]);

  React.useEffect(() => {
    void loadCredentials();
  }, [loadCredentials]);

  const withElevation = async (action: () => Promise<void>) => {
    const result = await requireElevatedAuth();
    if (!result.ok) {
      toast.error(result.message || 'Verification required');
      return;
    }
    await action();
  };

  const handleEnroll = async () => {
    if (!standalone) {
      toast.error(`${faceIdLabel} unlock is only available in the installed app`);
      return;
    }
    if (!online) {
      toast.error('You must be online to change security settings.');
      return;
    }
    setBusy(true);
    try {
      await withElevation(async () => {
        const { prfEnabled } = await enrollFaceId();
        toast.success(
          prfEnabled
            ? `${faceIdLabel} enabled — including offline unlock`
            : `${faceIdLabel} enabled — unlock while online`
        );
        await loadCredentials();
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Could not enable ${faceIdLabel}`);
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    if (!online) {
      toast.error('You must be online to change security settings.');
      return;
    }
    setBusy(true);
    try {
      await withElevation(async () => {
        await disableFaceId();
        toast.success(`${faceIdLabel} disabled on this device`);
        setShowDisableConfirm(false);
        await loadCredentials();
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Could not disable ${faceIdLabel}`);
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (id: string) => {
    if (!online) {
      toast.error('You must be online to change security settings.');
      return;
    }
    setBusy(true);
    try {
      await withElevation(async () => {
        await webauthnApi.deleteCredential(id);
        toast.success('Device credential removed');
        setRevokeId(null);
        await loadCredentials();
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not revoke credential');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Security</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!online && (
            <p className="text-sm text-muted-foreground">
              You must be online to change security settings.
            </p>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">{faceIdLabel} unlock</p>
                <p className="text-xs text-muted-foreground">
                  {standalone
                    ? isFaceIdEnrolled
                      ? canOfflineUnlock
                        ? 'Enabled on this device (offline unlock available)'
                        : 'Enabled on this device (online unlock only)'
                      : 'Optional unlock for the installed app'
                    : 'Available after installing Balance as an app'}
                </p>
              </div>
              {standalone && online && (
                isFaceIdEnrolled ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDisableConfirm(true)}
                    disabled={busy}
                  >
                    Disable
                  </Button>
                ) : (
                  <Button size="sm" onClick={() => void handleEnroll()} loading={busy}>
                    Enable
                  </Button>
                )
              )}
            </div>
          </div>

          {online && sessionState === 'onlineAuthenticated' && (
            <div className="space-y-2 border-t border-border pt-3">
              <p className="text-sm font-medium">Trusted devices</p>
              {loading ? (
                <p className="text-xs text-muted-foreground">Loading…</p>
              ) : credentials.length === 0 ? (
                <p className="text-xs text-muted-foreground">No Face ID devices registered yet.</p>
              ) : (
                <ul className="space-y-2">
                  {credentials.map((cred) => (
                    <li
                      key={cred.id}
                      className="flex items-center justify-between gap-2 text-sm"
                    >
                      <span className="truncate">
                        {cred.deviceName || faceIdLabel}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRevokeId(cred.id)}
                        disabled={busy}
                      >
                        Revoke
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDisableConfirm} onOpenChange={setShowDisableConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disable {faceIdLabel}?</DialogTitle>
            <DialogDescription>
              This removes unlock from this device. Unsynced changes on this device will be
              permanently deleted if you continue with pending offline edits.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisableConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" loading={busy} onClick={() => void handleDisable()}>
              Disable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!revokeId} onOpenChange={(open) => !open && setRevokeId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke device?</DialogTitle>
            <DialogDescription>
              That device will need to sign in with Google again. Unsynced changes on this device
              will be permanently deleted if local vault data is cleared.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              loading={busy}
              onClick={() => revokeId && void handleRevoke(revokeId)}
            >
              Revoke
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
